/**
 * PortalAppShell — shared wrappers used by every per-portal App.<portal>.tsx
 * (Phase 2.5 split-bundle). Lovable preview vẫn dùng src/App.tsx full-routes.
 *
 * Mỗi portal app cung cấp <Routes> riêng → bundle dist/<portal>/ chỉ chứa
 * code routes/pages của portal đó (cộng shared providers & UI primitives).
 */
import { Suspense, useEffect, type ReactNode } from "react";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { Loader2 } from "lucide-react";

import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import ScrollToTop from "@/components/ScrollToTop";
import AppErrorBoundary from "@/components/system/AppErrorBoundary";

const queryClient = new QueryClient();

const PORTAL_FIXED_TITLES: Record<string, string> = {
  admin: "Admin Login | KhmerHealth",
  vendor: "Vendor Login | KhmerHealth",
  hospital: "Hospital Login | KhmerHealth",
  guest: "Guest Login | KhmerHealth",
  agent: "Agent Login | KhmerHealth",
};

/**
 * TitleLock — forces document.title to the fixed per-portal value and
 * keeps it locked even if downstream <Helmet> or other code tries to
 * change it. Uses a MutationObserver on <head> to re-assert the title.
 */
const TitleLock = ({ portal }: { portal: string }) => {
  useEffect(() => {
    const fixed = PORTAL_FIXED_TITLES[portal];
    if (!fixed) return;
    const apply = () => {
      if (document.title !== fixed) document.title = fixed;
    };
    apply();
    const obs = new MutationObserver(apply);
    obs.observe(document.head, { childList: true, subtree: true, characterData: true });
    return () => obs.disconnect();
  }, [portal]);
  return null;
};

const LazyFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

interface PortalAppShellProps {
  portal: string;
  children: ReactNode;
}

export const PortalAppShell = ({ portal, children }: PortalAppShellProps) => {
  console.log(`[PortalApp:${portal}] Rendering`);
  return (
    <AppErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <ScrollToTop />
              <TitleLock portal={portal} />
              <Suspense fallback={<LazyFallback />}>{children}</Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </AppErrorBoundary>
  );
};

export default PortalAppShell;
